import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminSidebar from '@/components/AdminSidebar';
import { Plus, Pencil, Trash2, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import ImageUpload from '@/components/ImageUpload';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const empty = { name: '', whatsapp: '', bidang: '', nip: '', foto: '' };

export default function ManajemenGuru() {
  const [guru, setGuru] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => { fetch(); }, []);

  const fetch = () => axios.get(`${API}/guru`).then(r => setGuru(r.data)).catch(() => {});

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) { await axios.put(`${API}/guru/${editId}`, form); toast.success('Guru diperbarui'); }
      else { await axios.post(`${API}/guru`, form); toast.success('Guru ditambahkan'); }
      setShowForm(false); setEditId(null); setForm(empty); fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Gagal menyimpan'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus guru ini?')) return;
    await axios.delete(`${API}/guru/${id}`);
    toast.success('Guru dihapus'); fetch();
  };

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content bg-gray-50 min-h-screen">
        <div className="bg-white border-b border-gray-100 px-4 md:px-8 py-4 md:py-6 flex items-start md:items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="font-playfair text-2xl font-bold text-emerald-950">Data Guru / Ustaz</h1>
            <p className="text-sm text-gray-500">{guru.length} guru terdaftar</p>
          </div>
          <button data-testid="add-guru-btn" onClick={() => { setForm(empty); setEditId(null); setShowForm(true); }}
            className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2.5 rounded-xl text-sm font-semibold shadow-emerald transition-all">
            <Plus size={16} /> Tambah Guru
          </button>
        </div>
        <div className="p-4 md:p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {guru.map((g, i) => (
              <motion.div key={g.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-white rounded-2xl p-6 shadow-card border border-gray-50">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-emerald overflow-hidden">
                  {g.foto ? (
                    <img src={g.foto} alt={g.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-white font-bold text-lg">{g.name?.charAt(0)}</span>
                  )}
                </div>
                  <div className="flex gap-1.5">
                    <button onClick={() => { setForm({ name: g.name, whatsapp: g.whatsapp||'', bidang: g.bidang||'', nip: g.nip||'', foto: g.foto||'' }); setEditId(g.id); setShowForm(true); }}
                      className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"><Pencil size={14} /></button>
                    <button onClick={() => handleDelete(g.id)} className="p-2 text-red-400 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={14} /></button>
                  </div>
                </div>
                <h3 className="font-semibold text-gray-800 text-sm">{g.name}</h3>
                {g.bidang && <p className="text-xs text-emerald-600 mt-0.5">{g.bidang}</p>}
                {g.whatsapp && (
                  <a href={`https://wa.me/${g.whatsapp}`} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 mt-3 text-xs text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-full hover:bg-emerald-100 transition-colors">
                    WhatsApp
                  </a>
                )}
              </motion.div>
            ))}
            {guru.length === 0 && (
              <div className="col-span-3 text-center py-16 text-gray-400 text-sm">Belum ada data guru</div>
            )}
          </div>
        </div>

        <AnimatePresence>
          {showForm && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4"
              onClick={e => e.target === e.currentTarget && setShowForm(false)}>
              <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                className="bg-white rounded-2xl shadow-xl w-full max-w-md">
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                  <h2 className="font-playfair text-xl font-bold text-emerald-950">{editId ? 'Edit Guru' : 'Tambah Guru'}</h2>
                  <button onClick={() => setShowForm(false)} className="p-2 hover:bg-gray-100 rounded-xl"><X size={18} /></button>
                </div>
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                  {[['name','Nama Lengkap *',true],['nip','NIP',false],['bidang','Bidang / Spesialisasi',false],['whatsapp','No. WhatsApp',false]].map(([key,label,req]) => (
                    <div key={key}>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">{label}</label>
                      <input required={req} value={form[key]} onChange={e => setForm(f => ({...f, [key]: e.target.value}))}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                    </div>
                  ))}
                  <ImageUpload value={form.foto} onChange={v => setForm(f => ({...f, foto: v}))} label="Foto Guru" />
                  <div className="flex gap-3 pt-2">
                    <button type="button" onClick={() => setShowForm(false)}
                      className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600">Batal</button>
                    <button data-testid="guru-form-submit" type="submit"
                      className="flex-1 py-2.5 bg-emerald-500 text-white rounded-xl text-sm font-semibold shadow-emerald flex items-center justify-center gap-2">
                      <Check size={15} /> Simpan
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
